/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Audio Storage Key Hierarchy & R2 Distribution DTOs (Task 7.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Functional categories for audio storage in Cloudflare R2 hierarchy.
 */
export const AudioStorageCategorySchema = z
  .enum(['tts', 'clones', 'mixes', 'stt'])
  .openapi({
    description: 'Storage category: tts (Text-to-Speech), clones (Voice Samples), mixes (Web Audio Mixer), stt (Whisper Transcriptions)',
    example: 'tts',
  });

export type AudioStorageCategory = z.infer<typeof AudioStorageCategorySchema>;

/**
 * Standard supported audio container formats.
 */
export const AudioContainerFormatSchema = z
  .enum(['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'webm'])
  .openapi({
    description: 'Audio file container format',
    example: 'mp3',
  });

export type AudioContainerFormat = z.infer<typeof AudioContainerFormatSchema>;

/**
 * Parameters for building hierarchical R2 storage keys.
 */
export const BuildAudioKeyParamsSchema = z
  .object({
    category: AudioStorageCategorySchema,
    workspaceId: z.string().min(1).openapi({ description: 'Workspace ID for multi-tenant isolation', example: 'ws_01' }),
    format: AudioContainerFormatSchema.default('mp3'),
    audioId: z.string().optional().openapi({ description: 'Audio asset ID (for TTS)', example: 'tts_audio_9988' }),
    voiceId: z.string().optional().openapi({ description: 'Cloned voice ID (for Voice Cloning)', example: 'voice_clone_123' }),
    sampleIndex: z.number().int().min(1).optional().openapi({ description: 'Sample index for cloned voice', example: 1 }),
    projectId: z.string().optional().openapi({ description: 'Mixer project ID (for Audio Mixes)', example: 'proj_mix_456' }),
    mixId: z.string().optional().openapi({ description: 'Rendered mix ID', example: 'mix_render_789' }),
    transcriptionId: z.string().optional().openapi({ description: 'Transcription task ID (for Whisper STT)', example: 'stt_task_001' }),
    date: z.string().optional().openapi({ description: 'ISO date string or timestamp for date-based partitioning', example: '2026-10-21' }),
  })
  .openapi('BuildAudioKeyParams');

export type BuildAudioKeyParamsDto = z.infer<typeof BuildAudioKeyParamsSchema>;

/**
 * Request payload for obtaining a presigned upload URL to Cloudflare R2.
 */
export const AudioStorageUploadUrlRequestSchema = z
  .object({
    category: AudioStorageCategorySchema,
    filename: z.string().min(1).max(255).openapi({ description: 'Original filename', example: 'interview_recording.mp3' }),
    format: AudioContainerFormatSchema.default('mp3'),
    fileSizeBytes: z
      .number()
      .int()
      .positive()
      .max(500 * 1024 * 1024, 'Audio file size exceeds maximum 500MB limit')
      .optional()
      .openapi({ description: 'Expected file size in bytes (up to 500MB for STT audio)', example: 25482000 }),
    projectId: z.string().optional().openapi({ description: 'Associated project ID if applicable', example: 'proj_mix_01' }),
    voiceId: z.string().optional().openapi({ description: 'Associated voice ID if cloning', example: 'voice_01' }),
    customMetadata: z.record(z.string()).optional().openapi({ description: 'Custom key-value metadata tags' }),
  })
  .openapi('AudioStorageUploadUrlRequest');

export type AudioStorageUploadUrlRequestDto = z.infer<typeof AudioStorageUploadUrlRequestSchema>;

/**
 * Response payload with presigned upload URL targeting Cloudflare R2.
 */
export const AudioStorageUploadUrlResponseSchema = z
  .object({
    uploadUrl: z.string().openapi({
      description: 'Presigned or direct endpoint URL for binary audio upload',
      example: 'https://api.sophiaplatform.com/api/v1/voice/storage/direct-upload?key=audio/...',
    }),
    r2Key: z.string().openapi({
      description: 'Structured storage key in Cloudflare R2 bucket',
      example: 'audio/ws_01/tts/2026/10/audio_12345.mp3',
    }),
    publicUrl: z.string().url().openapi({
      description: 'Public Zero-Egress CDN URL with Range request and CORS support',
      example: 'https://media.sophiaplatform.com/audio/ws_01/tts/2026/10/audio_12345.mp3',
    }),
    expiresInSeconds: z.number().int().openapi({ description: 'Upload authorization lifetime in seconds (15-30 min)', example: 1800 }),
    maxFileSizeBytes: z.number().int().openapi({ description: 'Allowed max file size in bytes', example: 524288000 }),
    mimeType: z.string().openapi({ description: 'Configured HTTP Content-Type header', example: 'audio/mpeg' }),
  })
  .openapi('AudioStorageUploadUrlResponse');

export type AudioStorageUploadUrlResponseDto = z.infer<typeof AudioStorageUploadUrlResponseSchema>;

/**
 * Metadata and distribution details for an audio object in R2.
 */
export const AudioStorageFileInfoResponseSchema = z
  .object({
    r2Key: z.string().openapi({ description: 'R2 storage key', example: 'audio/ws_01/tts/2026/10/audio_123.mp3' }),
    publicUrl: z.string().url().openapi({ description: 'Zero-Egress CDN URL', example: 'https://media.sophiaplatform.com/audio/ws_01/tts/2026/10/audio_123.mp3' }),
    mimeType: z.string().openapi({ description: 'MIME content type', example: 'audio/mpeg' }),
    sizeBytes: z.number().int().min(0).openapi({ description: 'Object size in bytes', example: 1048576 }),
    etag: z.string().optional().openapi({ description: 'R2 HTTP ETag', example: '"e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"' }),
    cacheControl: z.string().openapi({ description: 'Cache-Control header', example: 'public, max-age=31536000, immutable' }),
    supportsRange: z.boolean().default(true).openapi({ description: 'Whether HTTP 206 Partial Content Range requests are enabled', example: true }),
    uploadedAt: z.string().optional().openapi({ description: 'ISO upload timestamp', example: '2026-10-21T10:00:00.000Z' }),
  })
  .openapi('AudioStorageFileInfoResponse');

export type AudioStorageFileInfoResponseDto = z.infer<typeof AudioStorageFileInfoResponseSchema>;
